import { useRef, type KeyboardEvent } from 'react';
import type { PortalTab, PortalTabKey } from './portalTabs';

/**
 * The portal's tab bar (BUY1). It draws exactly the tabs it is handed and says which one is open; `tabsOf` decides
 * whether a page has tabs at all, and the page decides what each tab holds. Nothing here names a section or a count.
 * (The file is not called PortalTabs: on a case-insensitive disk that import path resolves to portalTabs.ts, the tab
 * MODEL beside it, and the import comes back undefined.)
 *
 * The page puts `role="tabpanel"`, `id={portalTabPanelId(active)}` and `aria-labelledby={portalTabId(active)}` on the
 * body the open tab controls; the selected tab's `aria-controls` points there. Arrow keys, Home and End move between
 * tabs (a tab opens when it is chosen, there is nothing to confirm).
 */

/** The DOM id of a tab button. One place, so the bar and the panel agree. */
export const portalTabId = (key: PortalTabKey): string => `portal-tab-${key}`;

/** The DOM id the page gives the open tab's panel. */
export const portalTabPanelId = (key: PortalTabKey): string => `portal-tabpanel-${key}`;

export default function PortalTabBar({
  tabs,
  active,
  onSelect,
}: {
  tabs: PortalTab[];
  active: PortalTabKey;
  onSelect: (key: PortalTabKey) => void;
}) {
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);

  // A bar of one tab is not a choice; the page only mounts this for a contract that declares its tabs.
  if (tabs.length < 2) return null;

  const choose = (index: number) => {
    const target = tabs[index];
    if (!target) return;
    buttons.current[index]?.focus();
    if (target.key !== active) onSelect(target.key);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    choose(next);
  };

  return (
    <div className="portal-tabs portal-no-print" role="tablist" aria-label="Portal pages">
      {tabs.map((tab, index) => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            ref={(el) => {
              buttons.current[index] = el;
            }}
            type="button"
            role="tab"
            id={portalTabId(tab.key)}
            className={`portal-tab${selected ? ' portal-tab-active' : ''}`}
            aria-selected={selected}
            aria-controls={selected ? portalTabPanelId(tab.key) : undefined}
            tabIndex={selected ? 0 : -1}
            onClick={() => choose(index)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
